import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { errMsg, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", category: "handmade", description: "", price: 0, discount_percent: 0, hpp: 0, stock: 0,
  weight_grams: 200, images: [], variants: [], featured: false, active: true };

const NumField = ({ label, k, form, setForm }) => (
  <div className="space-y-1.5"><Label>{label}</Label>
    <Input type="number" min="0" data-testid={`product-form-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} />
  </div>
);

const ImagesField = ({ images, onChange }) => {
  const call = useApi();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        urls.push((await call("post", "/admin/upload", fd)).url);
      }
      onChange([...images, ...urls]);
    } catch (err) { toast.error(errMsg(err, "Upload gagal")); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-1.5">
      <Label>Foto produk (foto pertama jadi foto utama)</Label>
      <div className="flex flex-wrap gap-2">
        {images.map((src, i) => (
          <div key={src + i} className="relative h-20 w-20 overflow-hidden rounded-md border border-stone-200">
            <img src={imgUrl(src)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5" data-testid={`product-form-remove-image-${i}`}><X className="h-3 w-3" /></button>
          </div>
        ))}
        <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-md border border-dashed border-stone-300 text-stone-500 hover:bg-stone-50">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <input type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="product-form-image-upload" />
        </label>
      </div>
    </div>
  );
};

const VariantsField = ({ variants, onChange }) => (
  <div className="space-y-2">
    <Label>Variasi (mis. Warna, Ukuran, Tipe Bahan) — pisahkan pilihan dengan koma</Label>
    {variants.map((v, i) => (
      <div key={i} className="flex gap-2">
        <Input className="w-32" placeholder="Warna" value={v.name} data-testid={`variant-name-${i}`} onChange={(e) => onChange(variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
        <Input placeholder="Merah, Biru, Hijau" value={v.raw ?? v.options.join(", ")} data-testid={`variant-options-${i}`}
          onChange={(e) => onChange(variants.map((x, j) => (j === i ? { ...x, raw: e.target.value } : x)))} />
        <Button type="button" variant="ghost" size="icon" onClick={() => onChange(variants.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" data-testid="add-variant-button" onClick={() => onChange([...variants, { name: "", options: [], raw: "" }])}><Plus className="mr-1 h-4 w-4" /> Tambah variasi</Button>
  </div>
);

export const ProductFormDialog = ({ open, onOpenChange, product, onSaved }) => {
  const call = useApi();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(product ? { ...EMPTY, ...product } : EMPTY); }, [open, product]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const variants = form.variants.map((v) => ({ name: v.name.trim(), options: (v.raw ?? v.options.join(",")).split(",").map((s) => s.trim()).filter(Boolean) })).filter((v) => v.name && v.options.length);
    const { id, final_price, sold, created_at, updated_at, deleted, ...rest } = form;
    try {
      const body = { ...rest, variants };
      if (product) await call("put", `/admin/products/${product.id}`, body); else await call("post", "/admin/products", body);
      toast.success("Produk disimpan");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err, "Gagal menyimpan")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{product ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Nama produk</Label><Input required data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Kategori</Label>
              <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="handmade">Produk Handmade</option><option value="bahan">Alat & Bahan</option>
              </select>
            </div>
            <NumField label="Harga jual (Rp)" k="price" form={form} setForm={setForm} />
            <NumField label="Diskon (%)" k="discount_percent" form={form} setForm={setForm} />
            <NumField label="Modal / HPP per item (Rp)" k="hpp" form={form} setForm={setForm} />
            <NumField label="Stok" k="stock" form={form} setForm={setForm} />
            <NumField label="Berat (gram)" k="weight_grams" form={form} setForm={setForm} />
          </div>
          <div className="space-y-1.5"><Label>Deskripsi</Label><Textarea rows={5} data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <ImagesField images={form.images} onChange={(images) => setForm({ ...form, images })} />
          <VariantsField variants={form.variants} onChange={(variants) => setForm({ ...form, variants })} />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-form-active" /> Tampil di toko</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-form-featured" /> Produk unggulan</label>
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="product-form-submit">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
